import {
	SCROLL_DEPTH_MILESTONES,
	type ScrollDepthMilestone,
} from '../trackScrollDepth/trackScrollDepth';

// Percentage of the scrollable distance currently scrolled through, 0-100.
// Uses the *scrollable* distance (total height minus one viewport) rather than
// total height, so the bottom of the page is 100% on every viewport size.
// Pages shorter than the viewport have nothing to scroll and count as fully
// read, which is what a reader of a short article has in fact done.
export const getScrollPercentage = ({
	scrollTop,
	viewportHeight,
	contentHeight,
}: {
	scrollTop: number;
	viewportHeight: number;
	contentHeight: number;
}) => {
	const scrollableDistance = contentHeight - viewportHeight;

	if (scrollableDistance <= 0) {
		return 100;
	}

	// Clamped because elastic/rubber-band scrolling reports positions past both
	// ends on iOS and macOS.
	return Math.min(100, Math.max(0, (scrollTop / scrollableDistance) * 100));
};

// Milestones at or below `percentage` that have not been reported yet.
// Returns every unreported one rather than just the highest, so a jump (anchor
// link, restored scroll position, a flick to the bottom) still reports the
// milestones it skipped over instead of silently losing them.
export const getMilestonesToReport = ({
	percentage,
	reported,
}: {
	percentage: number;
	reported: ReadonlySet<ScrollDepthMilestone>;
}) =>
	SCROLL_DEPTH_MILESTONES.filter(
		(milestone) => percentage >= milestone && !reported.has(milestone)
	);

export interface ObserveScrollDepthOptions {
	// The scroll container. The article routes scroll the window, but the
	// in-app (mobile-article) surface scrolls an overlay element instead.
	target?: HTMLElement | Window;
	onMilestone: (milestone: ScrollDepthMilestone) => void;
}

// Watches `target` and calls `onMilestone` once per milestone crossed. Returns
// a teardown function. Browser-only: the article pages are ISR-cached, so all
// scroll instrumentation has to run client-side.
export const observeScrollDepth = ({
	target = window,
	onMilestone,
}: ObserveScrollDepthOptions) => {
	const reported = new Set<ScrollDepthMilestone>();

	const readGeometry = () => {
		if (target === window) {
			const doc = document.documentElement;

			return {
				scrollTop: window.scrollY,
				viewportHeight: window.innerHeight,
				contentHeight: doc.scrollHeight,
			};
		}

		const element = target as HTMLElement;

		return {
			scrollTop: element.scrollTop,
			viewportHeight: element.clientHeight,
			contentHeight: element.scrollHeight,
		};
	};

	// rAF-throttled: scroll fires far more often than once per frame, and each
	// handler run reads layout properties that would otherwise thrash.
	let frame: number | undefined;
	let pending = false;

	const measure = () => {
		const milestones = getMilestonesToReport({
			percentage: getScrollPercentage(readGeometry()),
			reported,
		});

		milestones.forEach((milestone) => {
			reported.add(milestone);
			onMilestone(milestone);
		});

		if (reported.size === SCROLL_DEPTH_MILESTONES.length) {
			// Every milestone is in — stop listening rather than measuring for
			// the rest of the visit.
			teardown();
		}
	};

	const handleScroll = () => {
		if (pending) {
			return;
		}

		pending = true;

		// `pending` is the throttle latch rather than the frame handle itself:
		// if the callback runs before requestAnimationFrame returns, assigning
		// its handle afterwards would re-latch the throttle and drop every
		// later scroll.
		frame = requestAnimationFrame(() => {
			pending = false;
			frame = undefined;

			measure();
		});
	};

	const teardown = () => {
		target.removeEventListener('scroll', handleScroll);
		window.removeEventListener('resize', handleScroll);

		pending = false;

		if (frame !== undefined) {
			cancelAnimationFrame(frame);
			frame = undefined;
		}
	};

	target.addEventListener('scroll', handleScroll, { passive: true });
	// Rotating or resizing changes the scrollable distance, which moves where
	// the milestones sit.
	window.addEventListener('resize', handleScroll, { passive: true });

	// Measure once up front: images and embeds settle after mount, and a
	// restored scroll position or a short article can already be past 25%.
	measure();

	return teardown;
};
